import { dayEndMood, moodColor, moodLabel, signedFigure } from '@adhd/shared';
import type { DayGlance, DayLineKind } from '@adhd/shared';

/**
 * The last week, one card per day, scrolled sideways.
 *
 * A week read at a glance rather than a day read in detail — which is a
 * different question and, for anyone worried about someone, usually the first
 * one: not "how is he right now" but "has this been going on".
 *
 * SCROLLED, NOT WRAPPED. A wrapped grid of seven days becomes a block of
 * content the eye has to work through, and on a phone it is most of a screen.
 * A single row stays one gesture long and one glance wide, and the days that
 * matter most — the recent ones — are the ones already on screen.
 *
 * NEWEST FIRST, LEFT TO RIGHT. Reversing to put today on the right would match
 * a calendar, and would also mean opening the strip already scrolled to the far
 * end or looking at last Tuesday first.
 *
 * A DAY WITH NOTHING IN IT IS DRAWN AS EMPTY, not as calm. Those are opposite
 * things — one is a quiet Tuesday, the other is a week where somebody stopped
 * logging — and a strip that renders them alike is worse than no strip, because
 * it invents a run of good days out of a gap in the record.
 */
export function DayStrip({
  days,
  /*
   * Colour the rail by mean mood where the verdict is withheld by the setting.
   *
   * A prop rather than a `useAi()` call inside this component, because a share
   * link renders this same strip with no session behind it — a hook reading the
   * owner's settings would be reaching for something that is not there, on the
   * one page where a crash is in front of somebody else.
   */
  moodRail = false,
  /** Shown above the row. Omitted where the surrounding section already says it. */
  title,
  /**
   * Makes each day a way in rather than a read-out.
   *
   * On the owner's own screen a week you can see but not open is a tease: the
   * interesting day is usually not today, and the only route to it was the Log.
   *
   * A share link passes one too, now. It did not, and the reason given was that
   * a recipient had nothing behind the tap to reach — true of the endpoint, and
   * it stayed true because the day's detail was being computed to draw these
   * cards and then discarded. It now travels with them, so the reason has gone
   * and the handler can come. Still omitted anywhere a strip is genuinely only
   * a strip: a button that opens nothing is the dead end this page exists to
   * avoid.
   */
  onPick,
  /** The day currently being shown below, so the row can say which. */
  picked,
  /**
   * A day the page already shows in full somewhere else.
   *
   * On a share link that is today, which has its own section above this strip.
   * Its card is still a control — a row where one card ignores you is worse
   * than the duplication it avoids, and worse still under a line inviting you
   * to open any of them — but pressing it takes you to the block that already
   * exists rather than building a second copy of it further down.
   *
   * So it is a button without `aria-pressed`: it selects nothing, and claiming
   * otherwise to a screen reader would be a lie about what it does.
   */
  alreadyShown,
  /**
   * The owner's today, where the reader may not be the owner.
   *
   * "Today" and "Yesterday" were computed from the reader's own clock. On a
   * share link that is somebody else's clock by definition: from Honolulu, a
   * London owner's two-crisis day was labelled "2 WED" and a calm day labelled
   * "TODAY" — on the same page whose table below dated both correctly.
   */
  today,
  /**
   * The label above each day's line, by its kind (DAY_LINE_LABEL), on a share
   * link at What my days held or above, where a day can carry either kind of
   * line. The owner's own screens do not pass it.
   */
  lineLabel,
}: {
  days: DayGlance[];
  lineLabel?: (kind: DayLineKind) => string;
  title?: string;
  onPick?: (day: string) => void;
  picked?: string | null;
  alreadyShown?: string | null;
  today?: string | null;
  moodRail?: boolean;
}): JSX.Element | null {
  const byMood = byMoodWhen(moodRail);
  if (days.length === 0) return null;

  return (
    <div className="stack-sm stack">
      {title && <h3 style={{ marginBottom: 0 }}>{title}</h3>}
      <div className="day-strip" role="list">
        {days.map((d) => {
          // A button only where there is somewhere to go, which is a decision
          // made by the caller rather than assumed here. Whether that
          // somewhere is a fold below or a block above is also theirs.
          const acts = Boolean(onPick);
          const toggles = acts && d.day !== alreadyShown;
          const Tag = acts ? 'button' : 'article';
          return (
          <Tag
            key={d.day}
            role="listitem"
            {...(acts && onPick
              ? {
                  onClick: () => onPick(d.day),
                  type: 'button' as const,
                  ...(toggles ? { 'aria-pressed': picked === d.day } : {}),
                }
              : {})}
            className={`day-strip-card${d.empty ? ' day-strip-empty' : ''}${
              d.state ? ` day-strip-${d.state}` : ''
            }${byMood(d) !== null ? ' day-strip-bymood' : ''}${
              acts ? ' day-strip-pickable' : ''
            }${picked === d.day ? ' day-strip-picked' : ''}`}
            style={byMood(d) !== null ? { borderLeftColor: moodColor(byMood(d)!) } : undefined}
          >
            <span className="day-strip-when">{shortDay(d.day, today)}</span>

            {d.empty ? (
              <span className="day-strip-nothing">Nothing logged</span>
            ) : (
              <>
                {dayEnd(d) !== null && (
                  <>
                    {/*
                      WHERE THE DAY ENDED, and the word for it.

                      Release 1 put the word here, because a recipient had been
                      given the integer and nothing else. Release 2 fixes the
                      integer: this printed `meanMood` through a signed() that
                      rounded nothing, so nine of his thirty-three logged days
                      showed a two-decimal figure as the biggest glyph on the
                      card — "-1.33" on a therapist's page, and an average of
                      the day rather than a rating he ever gave. help.html has
                      always said the average is the least useful number here
                      and that where a day ENDED tells you more.

                      The word is small rather than beside the figure because
                      the card is 8.5rem wide and "A bit low" does not fit at
                      --text-xl.
                    */}
                    <span className="day-strip-mood">{signed(dayEnd(d)!)}</span>
                    <span className="day-strip-word">{moodLabel(dayEnd(d)!)}</span>
                  </>
                )}
                {/*
                  A day with something on it, whose something this reader was
                  not given.

                  `empty` is counted server-side from a total that is never
                  stripped, so the card correctly takes this branch — but every
                  count inside it is null, `countLine` finds nothing above zero,
                  and the card rendered as a date and white space. On Wren's
                  week the single day she wrote on was the only mute card in a
                  row of "Nothing logged", which reads as less than the days
                  with nothing on them.

                  The fold underneath already says this properly; the strip
                  exists to be read at a glance, which is where it mattered.
                */}
                <span className="day-strip-counts">{countLine(d) || 'Not shared'}</span>
              </>
            )}

            {/* Only where one had already been written. The strip never asks for
                a sentence — see dayGlances — so most past days carry figures
                alone, and that is the normal state of the past rather than a
                gap. */}
            {d.headline && lineLabel && d.lineKind && (
              <span className="sv-line-kind">{lineLabel(d.lineKind)}</span>
            )}
            {d.headline && <span className="day-strip-line">{d.headline}</span>}
          </Tag>
          );
        })}
      </div>
    </div>
  );
}

/** "Today", "Yesterday", then "Fri 15" — the recent days are the ones scanned. */
function shortDay(day: string, ownerToday?: string | null): string {
  // The owner's day where one was given, the reader's only as a fallback for
  // the owner's own screens, where the two are the same person.
  const today = ownerToday ?? new Date().toLocaleDateString('en-CA');
  if (day === today) return 'Today';
  const yesterday = new Date(`${today}T12:00:00`);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day === yesterday.toLocaleDateString('en-CA')) return 'Yesterday';
  // Parsed as local midday rather than midnight: a bare YYYY-MM-DD is UTC, and
  // west of Greenwich that lands on the previous evening and prints the wrong
  // weekday.
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
  });
}

/* One definition, in @adhd/shared. The local copy rounded nothing while
   SharedView's rounded to zero digits, so the same day could print "-1.33" on
   a card and "-1" in the table three centimetres below it. */
const signed = (n: number): string => signedFigure(n);

/* Moved to @adhd/shared as `dayEndMood`: the Log's day heading is a third call
   site, in a file that could not import it from here, and it drifted to the
   mean. See the comment on the function. */
const dayEnd = dayEndMood;

/**
 * The two or three counts worth a glance, and nothing else.
 *
 * A card wide enough for every figure would defeat the strip; the detail lives
 * one tap away in the Log, where there is room for it.
 */
/**
 * The colour of a day's rail when there is no verdict to colour it by.
 *
 * `state` is the model's read of the day and it is withheld below `reflect`
 * (status.ts) — correctly, since it is a judgement nobody made. But the rail
 * then carried nothing at all, on the one element whose whole job is being
 * glanced at, while the day's mean mood sat right there unused and unwritten by
 * any model.
 *
 * Only when the setting is what withheld it. A day with no verdict at a high
 * setting is a day nothing was written about, and that is a real thing for the
 * rail to say by staying blank — different from "you asked me not to judge".
 */
function byMoodWhen(on: boolean) {
  return (d: DayGlance): number | null =>
    on && d.state === null && d.meanMood !== null ? d.meanMood : null;
}

function countLine(d: DayGlance): string {
  const bits: string[] = [];
  if (d.checkins > 0) bits.push(`${d.checkins} check-in${d.checkins === 1 ? '' : 's'}`);
  // "Help now", the name on the button and the nav tab. "SOS" appeared on the
  // home screen and twice in the clinician's document, and never once on the
  // screen it names — so a reader saw "9 SOS" having pressed nothing called SOS.
  if (d.sosEvents > 0) bits.push(`${d.sosEvents} Help now`);
  /*
   * Writing counts, whether or not he also rated himself.
   *
   * This sat behind `bits.length === 0`, so entries were named only on days he
   * had done nothing else — which meant a day holding sixteen journal entries
   * and five check-ins read "5 check-ins", and his writing was invisible on
   * Today twice over: absent from the list below and unmentioned here.
   *
   * After Help now, which keeps its place, and before records.
   */
  if (d.journalEntries > 0) {
    bits.push(`${d.journalEntries} entr${d.journalEntries === 1 ? 'y' : 'ies'}`);
  }
  // "thought record", the same words the Log uses. The strip said "1 record"
  // and the Log said "1 thought record" about the same day, and a reader has no
  // way to know those are the same thing rather than two kinds of thing.
  if (d.thoughtRecords > 0) {
    bits.push(`${d.thoughtRecords} thought record${d.thoughtRecords === 1 ? '' : 's'}`);
  }
  if (bits.length === 0 && d.medsScheduled > 0) {
    // Named where none of it was confirmed. "1/1 doses" on a day nobody opened
    // the app reads as a tick somebody earned, and on the week strip it sits
    // directly beside days that were genuinely logged.
    const allAssumed = d.medsTaken > 0 && d.medsAssumed >= d.medsTaken;
    bits.push(`${d.medsTaken}/${d.medsScheduled} doses${allAssumed ? ', assumed' : ''}`);
  }
  return bits.slice(0, 3).join(' · ');
}
